import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Button, Form, Spinner, Badge, Row, Col, Modal
} from 'react-bootstrap';
import axios from 'axios';
import Cookies from 'js-cookie';
import '../ChatApp.css';
import '../DynamicTemplates.css';
import logo from '../images/scalex_ai_logo.png';
import { showToast as toast } from '../toastManager';
import errorMessages from '../errorMessages.js';

// ─── Asset Picker helpers ─────────────────────────────────────────────────────

function getYouTubeThumbnail(url) {
    const m = url?.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=))([^&?/]+)/);
    return m ? `https://img.youtube.com/vi/${m[1]}/mqdefault.jpg` : null;
}

function AssetPickerGroup({ label, onSelectAll, disabled, children, containerStyle }) {
    return (
        <div style={{ marginBottom: 12 }}>
            {(label || onSelectAll) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    {label && (
                        <span style={{ fontSize: 10, fontWeight: 700, color: '#9aa2ad', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            {label}
                        </span>
                    )}
                    {onSelectAll && !disabled && (
                        <button
                            type="button"
                            onClick={onSelectAll}
                            style={{ fontSize: 10, padding: '1px 6px', border: '1px solid #dee2e6', borderRadius: 3, background: '#fff', cursor: 'pointer', color: '#495057', lineHeight: '18px' }}
                        >
                            Select All
                        </button>
                    )}
                </div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, ...containerStyle }}>
                {children}
            </div>
        </div>
    );
}

function AssetPickerCard({ id, name, previewType, previewUrl, assetObj, subLabel, selected, disabled, onToggle, large }) {
    const [imgErr, setImgErr] = React.useState(false);

    const cardWidth = large ? '100%' : 110;
    const previewH  = large ? 160 : 80;
    const scale     = 0.333;

    const iconFor = { image: '🖼️', video: '🎬', pdf: '📄', section: '🧩', form: '📝' };
    const ytThumb = previewType === 'video' ? getYouTubeThumbnail(previewUrl) : null;
    const effectiveUrl = ytThumb || previewUrl;

    return (
        <div
            onClick={() => !disabled && onToggle(id, assetObj)}
            title={name}
            style={{
                width: cardWidth,
                border: selected ? '2px solid #0d6efd' : '1px solid #dee2e6',
                borderRadius: 8,
                overflow: 'hidden',
                background: selected ? '#e8f0fe' : '#fff',
                cursor: disabled ? 'default' : 'pointer',
                flexShrink: 0,
                userSelect: 'none',
                transition: 'border-color 0.12s, background 0.12s',
            }}
        >
            <div style={{ height: previewH, background: '#f0f2f5', position: 'relative', overflow: 'hidden' }}>
                {previewType === 'section' && previewUrl ? (
                    <iframe
                        src={previewUrl}
                        title={name}
                        sandbox="allow-scripts allow-same-origin"
                        style={{ width: `${100 / scale}%`, height: `${100 / scale}%`, transform: `scale(${scale})`, transformOrigin: '0 0', border: 'none', pointerEvents: 'none' }}
                    />
                ) : (previewType === 'image' || previewType === 'video') && effectiveUrl && !imgErr ? (
                    <img
                        src={effectiveUrl}
                        alt={name}
                        onError={() => setImgErr(true)}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontSize: large ? 36 : 28 }}>
                        {previewType === 'special' ? (assetObj?.icon || '📋') : (iconFor[previewType] || '📋')}
                    </div>
                )}
                {selected && (
                    <div style={{
                        position: 'absolute', top: 4, right: 4,
                        background: 'var(--cux-accent, #0d6efd)', color: '#fff',
                        borderRadius: '50%', width: 18, height: 18,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 10, fontWeight: 700, lineHeight: 1,
                    }}>✓</div>
                )}
            </div>
            <div style={{ padding: '5px 7px' }}>
                <div style={{ fontSize: 11, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#212529' }}>
                    {name}
                </div>
                {subLabel && <div style={{ fontSize: 10, color: '#6c757d', marginTop: 1 }}>{subLabel}</div>}
            </div>
        </div>
    );
}

// ─── Constants ────────────────────────────────────────────────────────────────

const ASSET_TYPE_OPTIONS = [
    { value: 'All', label: 'All' },
    { value: 'Forms', label: 'Forms' },
    { value: 'Image', label: 'Image' },
    { value: 'PDF', label: 'PDF' },
    { value: 'Sections', label: 'Sections' },
    { value: 'Video', label: 'Video' },
];

const OUTPUT_TYPE_OPTIONS = [
    { value: 'sections', label: 'Sections' },
    { value: 'faqs', label: 'FAQs' },
    { value: 'jumplinks', label: 'JumpLinks' },
    { value: 'forms', label: 'Forms' },
];

const LLM_PROVIDERS = {
    openai:    { label: 'OpenAI', models: [
        { value: 'gpt-4.1',            label: 'GPT-4.1 (latest)' },
        { value: 'gpt-4o',             label: 'GPT-4o' },
        { value: 'gpt-4o-mini',        label: 'GPT-4o Mini' },
    ]},
    anthropic: { label: 'Anthropic Claude', models: [
        { value: 'claude-opus-4-7',           label: 'Claude Opus 4.7 (latest)' },
        { value: 'claude-sonnet-4-6',         label: 'Claude Sonnet 4.6' },
        { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5' },
    ]},
    gemini:    { label: 'Google Gemini', models: [
        { value: 'gemini-3.1-pro-preview',   label: 'Gemini 3.1 Pro (latest)' },
        { value: 'gemini-2.5-pro',   label: 'Gemini 2.5 Pro' },
        { value: 'gemini-1.5-pro',   label: 'Gemini 1.5 Pro' },
    ]},
};

const SENSITIVE_KEYS = ['api_key', 'api_secret', 'secret', 'password', 'token', 'auth_token', 'endpoint_key'];

function stripSensitive(obj) {
    if (!obj || typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj.map(stripSensitive);
    return Object.fromEntries(
        Object.entries(obj)
            .filter(([k]) => !SENSITIVE_KEYS.includes(k.toLowerCase()))
            .map(([k, v]) => [k, stripSensitive(v)])
    );
}

// ─── JumpLinks helper ─────────────────────────────────────────────────────────

function buildJumpLinks(fallbackSections = []) {
    try {
        const sxi_content = Cookies.get('sxi_content');
        const flags = sxi_content ? (window.cognitiveUX?.featureFlags?.[sxi_content] || null) : null;

        // Primary: rearrangement / rearrangement_mobile from feature flags (items: { label, selector })
        if (flags) {
            const isMobile = window.innerWidth < 768;
            const rearr = isMobile ? flags.rearrangement_mobile : flags.rearrangement;
            const primary = (rearr || [])
                .map(s => ({ section_name: s.label, section_query_selector: s.selector }))
                .filter(s => s.section_name || s.section_query_selector);
            if (primary.length > 0) return primary;
        }

        // Fallback: sections_rearrangement fetched from MongoDB experiment
        // (items: { section_name, section_query_selector, section_id, ... })
        if (Array.isArray(fallbackSections) && fallbackSections.length > 0) {
            return fallbackSections
                .map(s => ({
                    section_name: s.section_name || s.label || '',
                    section_query_selector: s.section_query_selector || s.selector || '',
                }))
                .filter(s => s.section_name || s.section_query_selector);
        }

        return [];
    } catch {
        return [];
    }
}

// ─── Component ────────────────────────────────────────────────────────────────

function AIAssetContentGenerator({ ViewedPage, shop_domain, app_url }) {
    ViewedPage(34);

    // ─── Session persistence ─────────────────────────────────────────────────
    const SESSION_KEY = `sxi_ai_gen_${shop_domain || 'default'}`;
    const _sessionRef = useRef(null);
    if (_sessionRef.current === null) {
        try { _sessionRef.current = JSON.parse(sessionStorage.getItem(SESSION_KEY)) || {}; }
        catch { _sessionRef.current = {}; }
    }
    const _s = _sessionRef.current;

    // --- Loading & View ---
    const [pageLoading, setPageLoading] = useState(false);
    const [activeView, setActiveView] = useState(_s.activeView || 'chat');
    const [isConfigOpen, setIsConfigOpen] = useState(_s.isConfigOpen || false);
    const [isPickerOpen, setIsPickerOpen] = useState(false);

    // --- Config state ---
    const [selectedPage, setSelectedPage] = useState(_s.selectedPage || '');
    const [assetTypeFilter, setAssetTypeFilter] = useState(_s.assetTypeFilter || 'All');
    const [selectedAssets, setSelectedAssets] = useState(_s.selectedAssets || []);
    const [outputType, setOutputType] = useState(_s.outputType || 'sections');
    const [mode, setMode] = useState(_s.mode || 'create');
    const [isConfigLocked, setIsConfigLocked] = useState(_s.isConfigLocked || false);
    const [llmProvider, setLlmProvider] = useState(_s.llmProvider || 'openai');
    const [llmModel, setLlmModel] = useState(_s.llmModel || 'gpt-4.1');
    const [sessionCacheId, setSessionCacheId] = useState(_s.sessionCacheId || null);

    // --- Data sources ---
    const [assets, setAssets] = useState([]);
    const [importedSections, setImportedSections] = useState([]);
    const [storeSettings, setStoreSettings] = useState({ brandingPages: [], businessContext: null, formsSettings: [], allTextContent: {} });
    const [sectionsRearrangement, setSectionsRearrangement] = useState([]);

    // --- Chat state (Tab 2) ---
    const [messages, setMessages] = useState(_s.messages || []);
    const [chatSummary, setChatSummary] = useState(_s.chatSummary || '');
    // lastExchange: { user: string, assistant: string (raw JSON from LLM) }
    const [lastExchange, setLastExchange] = useState(_s.lastExchange || null);
    const [input, setInput] = useState('');
    const [generating, setGenerating] = useState(false);

    // --- Generated output ---
    const [generatedSections, setGeneratedSections] = useState(_s.generatedSections || []);

    const messagesEndRef = useRef(null);
    const pollIntervalRef = useRef(null);
    const inputRef = useRef(null);

    // ─── Load data on mount ──────────────────────────────────────────────────

    useEffect(() => {
        if (shop_domain && app_url) {
            loadAllData();
        }
    }, [shop_domain, app_url]);

    const loadAllData = async () => {
        setPageLoading(true);
        try {
            await Promise.all([loadAssets(), loadImportedSections(), loadStoreSettings()]);
        } finally {
            setPageLoading(false);
        }
    };

    const loadAssets = async () => {
        try {
            const res = await axios.post(`${app_url}/api/assets/get_all`, { shop_domain }, { withCredentials: true });
            if (res.data.success) setAssets(res.data.data || []);
        } catch {
            toast.error('Failed to load assets');
        }
    };

    const loadImportedSections = async () => {
        try {
            const res = await axios.post(`${app_url}/api/firebase/get_sections`, { shop_domain: Cookies.get('shop') }, { withCredentials: true });
            if (res.data.success) setImportedSections(res.data.data || []);
        } catch {
            // non-fatal
        }
    };

    const loadStoreSettings = async () => {
        try {
            const res = await axios.get(`${app_url}/api/store/ai-context`, { withCredentials: true });
            if (res.data.success) setStoreSettings(res.data.data);
        } catch {
            // non-fatal
        }
    };

    // Fetch sections_rearrangement from MongoDB whenever the selected page changes
    useEffect(() => {
        if (!selectedPage || !app_url) return;
        axios.get(`${app_url}/api/ai_generation/sections_rearrangement`, {
            params: { page: selectedPage },
            withCredentials: true,
        }).then(res => {
            if (res.data.success) setSectionsRearrangement(res.data.data || []);
        }).catch(() => {
            // non-fatal — jumplinks will rely on featureFlags only
        });
    }, [selectedPage, app_url]);

    // ─── Persist session to sessionStorage ──────────────────────────────────

    useEffect(() => {
        try {
            sessionStorage.setItem(SESSION_KEY, JSON.stringify({
                selectedPage,
                assetTypeFilter,
                selectedAssets,
                outputType,
                mode,
                isConfigLocked,
                isConfigOpen,
                llmProvider,
                llmModel,
                sessionCacheId,
                activeView,
                messages,
                chatSummary,
                lastExchange,
                generatedSections,
            }));
        } catch { /* storage quota exceeded or disabled — ignore */ }
    }, [selectedPage, assetTypeFilter, selectedAssets, outputType, mode, isConfigLocked, isConfigOpen, llmProvider, llmModel, sessionCacheId, activeView, messages, chatSummary, lastExchange, generatedSections]);

    // ─── Auto-scroll chat ────────────────────────────────────────────────────

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
        }
    }, [messages]);

    // Auto-grow the prompt textarea up to its CSS max-height as the user types / on clear
    useEffect(() => {
        const el = inputRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
    }, [input]);

    useEffect(() => {
        return () => { if (pollIntervalRef.current) clearInterval(pollIntervalRef.current); };
    }, []);

    // ─── Asset picker toggle ─────────────────────────────────────────────────

    const handleAssetToggle = useCallback((id, assetObj = null) => {
        if (isConfigLocked) return;

        if (id === 'choose_all_images') { setSelectedAssets(assets.filter(a => a.type === 'Image')); return; }
        if (id === 'choose_all_videos') { setSelectedAssets(assets.filter(a => a.type === 'Video' || a.type === 'Video Link')); return; }
        if (id === 'choose_all_pdfs') { setSelectedAssets(assets.filter(a => a.type === 'PDF')); return; }

        const alreadySelected = selectedAssets.some(a => {
            if (id === '__business_context__') return a._type === 'business_context';
            if (id === '__text_content__') return a._type === 'text_content';
            if (id.startsWith('form__')) return a._type === 'form' && `form__${a.form_id}` === id;
            if (id.startsWith('sec__')) return a._type === 'section' && `sec__${a.section_id}` === id;
            return a._id === id;
        });

        if (alreadySelected) {
            setSelectedAssets(prev => prev.filter(a => {
                if (id === '__business_context__') return a._type !== 'business_context';
                if (id === '__text_content__') return a._type !== 'text_content';
                if (id.startsWith('form__')) return !(a._type === 'form' && `form__${a.form_id}` === id);
                if (id.startsWith('sec__')) return !(a._type === 'section' && `sec__${a.section_id}` === id);
                return a._id !== id;
            }));
            return;
        }

        let newAsset;
        if (id === '__business_context__') newAsset = { _type: 'business_context', value: storeSettings.businessContext };
        else if (id === '__text_content__') newAsset = { _type: 'text_content', value: storeSettings.allTextContent?.[selectedPage] };
        else newAsset = assetObj;
        if (!newAsset) return;

        const incoming = [...selectedAssets, newAsset];
        const sections = incoming.filter(a => a._type === 'section');
        const forms = incoming.filter(a => a._type === 'form');
        if (sections.length > 1) { toast.error('Only 1 section can be selected at a time.'); return; }
        if (forms.length > 1) { toast.error('Only 1 form can be selected at a time.'); return; }
        setSelectedAssets(incoming);
    }, [isConfigLocked, selectedAssets, assets, storeSettings, selectedPage]);

    // ─── Config validation ───────────────────────────────────────────────────

    const validateConfig = () => {
        if (!selectedPage) {
            toast.error('Please select a page');
            return false;
        }
        if (outputType === 'forms') {
            const hasForm = selectedAssets.some(a => a._type === 'form');
            if (!hasForm) {
                toast.error('Output type is "Forms" — please select a form from the assets picker (2b)');
                return false;
            }
        }
        if (mode === 'improve') {
            const hasSection = selectedAssets.some(a => a._type === 'section');
            if (!hasSection) {
                toast.error('Mode is "Improve Existing" — please select a section from the assets picker (2b)');
                return false;
            }
        }
        return true;
    };

    const handleSaveConfig = () => {
        if (!validateConfig()) return;
        setSessionCacheId(crypto.randomUUID());
        setIsConfigLocked(true);
        setIsConfigOpen(false);
    };

    const handleResetConfig = async () => {
        if (sessionCacheId) {
            try { await axios.delete(`${app_url}/api/ai_generation/cache/${sessionCacheId}`, { withCredentials: true }); }
            catch { /* TTL will expire it naturally */ }
        }
        sessionStorage.removeItem(SESSION_KEY);
        _sessionRef.current = {};
        setSessionCacheId(null);
        setIsConfigLocked(false);
        setIsConfigOpen(true);
        setActiveView('chat');
        setMessages([]);
        setGeneratedSections([]);
        setChatSummary('');
        setLastExchange(null);
        setSelectedPage('');
        setAssetTypeFilter('All');
        setSelectedAssets([]);
        setOutputType('sections');
        setMode('create');
        setLlmProvider('openai');
        setLlmModel('gpt-4.1');
    };

    const handleProviderChange = (e) => {
        const p = e.target.value;
        setLlmProvider(p);
        setLlmModel(LLM_PROVIDERS[p].models[0].value);
    };

    // ─── File preview URL ────────────────────────────────────────────────────

    const getFilePreviewUrl = (asset) => {
        if (['PDF', 'Image'].includes(asset.type) && asset.fileName) {
            return `${app_url}/api/assets/file/${asset._id}?shop_domain=${shop_domain}`;
        }
        return null;
    };

    // ─── Build payload for generation ───────────────────────────────────────

    const buildPayload = (userMessage) => {
        const images = selectedAssets.filter(a => a.type === 'Image');
        const videos = selectedAssets.filter(a => a.type === 'Video' || a.type === 'Video Link');
        const pdfs = selectedAssets.filter(a => a.type === 'PDF');
        const sections = selectedAssets.filter(a => a._type === 'section');
        const forms = selectedAssets.filter(a => a._type === 'form');
        const bcItem = selectedAssets.find(a => a._type === 'business_context');
        const tcItem = selectedAssets.find(a => a._type === 'text_content');

        const jumpLinks = outputType === 'jumplinks' ? buildJumpLinks(sectionsRearrangement) : [];
        if (outputType === 'jumplinks' && jumpLinks.length === 0) {
            toast.info('JumpLinks: no section data found — generating based on assets only');
        }

        // Derive the section to improve/reference from assets (if any)
        const sectionAsset = sections[0] || null;

        return {
            page: selectedPage,
            outputType,
            mode,
            selectedAssets: {
                businessContext: bcItem?.value || null,
                textContent: tcItem?.value || null,
                forms: stripSensitive(forms),
                images: images.map(a => ({
                    title: a.title,
                    description: a.description || null,
                    url: a.url || (a.fileName ? getFilePreviewUrl(a) : null),
                })),
                videos: videos.map(a => ({ title: a.title, url: a.url })),
                pdfs: pdfs.map(a => ({ title: a.title })),
                sections: sections.map(s => ({
                    section_id: s.section_id,
                    section_name: s.section_name,
                    url: s.url || '',
                })),
            },
            // selectedSection carries url + name — backend fetches the HTML server-side
            selectedSection: mode === 'improve' && sectionAsset
                ? { url: sectionAsset.url || '', section_id: sectionAsset.section_id, section_name: sectionAsset.section_name }
                : null,
            jumpLinks,
            // Pass only the last exchange + rolling summary — not the full history
            chatContext: {
                lastExchange: lastExchange || null,
                summary: chatSummary || '',
            },
            llmProvider,
            llmModel,
            sessionCacheId,
            userMessage,
        };
    };

    // ─── Generate ────────────────────────────────────────────────────────────

    const handleGenerate = async (e) => {
        e?.preventDefault();
        if (!input.trim() || !isConfigLocked) return;

        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }

        const userMessage = input.trim();
        setInput('');
        setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
        setGenerating(true);
        setActiveView('sections');

        try {
            const payload = buildPayload(userMessage);
            const res = await axios.post(`${app_url}/api/ai_generation/generate_sections`, payload, { withCredentials: true });

            if (!res.data.success) throw new Error(res.data.message || 'Generation failed');

            const { jobId } = res.data;
            let ticks = 0;
            const MAX_TICKS = 300; // ~10 min at 2s intervals

            pollIntervalRef.current = setInterval(async () => {
                ticks++;
                try {
                    const pollRes = await axios.get(`${app_url}/api/ai_generation/job/${jobId}`, { withCredentials: true });
                    const job = pollRes.data.job || {};

                    if (job.status === 'succeeded') {
                        clearInterval(pollIntervalRef.current);
                        pollIntervalRef.current = null;

                        let sections = job.result?.sections || [];
                        const rawJson = job.result?.rawJson || '';

                        // Inject form endpoint URL client-side (api_key never sent to LLM)
                        const formAsset = selectedAssets.find(a => a._type === 'form');
                        if (outputType === 'forms' && formAsset?.endpoint?.url) {
                            sections = sections.map(s => ({
                                ...s,
                                html: (s.html || '').replace('__FORM_ACTION__', formAsset.endpoint.url),
                            }));
                        }

                        setGeneratedSections(sections);
                        setLastExchange({ user: userMessage, assistant: rawJson });

                        const usage = job.result?.usage || null;

                        let usageLine = '';
                        if (usage) {
                            const total  = (usage.input_tokens || 0) + (usage.output_tokens || 0);
                            const cached = usage.cache_read_tokens || usage.cached_tokens || usage.cache_read_input_tokens || 0;
                            usageLine = `\n\nTokens: ${total.toLocaleString()} total (${(usage.input_tokens || 0).toLocaleString()} in · ${(usage.output_tokens || 0).toLocaleString()} out${cached ? ` · ${cached.toLocaleString()} cached` : ''})`;
                        }

                        const assistantContent = sections.length
                            ? `Generated ${sections.length} section${sections.length > 1 ? 's' : ''}:\n` +
                              sections.map(s => `• ${s.section_name || 'Section'} (${s.section_type})`).join('\n') +
                              usageLine
                            : 'No sections were generated.';
                        setMessages(prev => [...prev, { role: 'assistant', content: assistantContent }]);
                        setGenerating(false);

                    } else if (job.status === 'failed') {
                        clearInterval(pollIntervalRef.current);
                        pollIntervalRef.current = null;
                        toast.error(errorMessages['AI_GENERATION_ERROR']?.title || 'Generation failed. Please try again.');
                        setMessages(prev => [...prev, { role: 'assistant', content: '⚠ Generation failed. Please try again.' }]);
                        setGenerating(false);

                    } else if (ticks >= MAX_TICKS) {
                        clearInterval(pollIntervalRef.current);
                        pollIntervalRef.current = null;
                        toast.error('Generation timed out. Please try again.');
                        setMessages(prev => [...prev, { role: 'assistant', content: '⚠ Generation timed out. Please try again.' }]);
                        setGenerating(false);
                    }
                } catch {
                    clearInterval(pollIntervalRef.current);
                    pollIntervalRef.current = null;
                    toast.error(errorMessages['AI_GENERATION_ERROR']?.title || 'Generation failed. Please try again.');
                    setMessages(prev => [...prev, { role: 'assistant', content: '⚠ Generation failed. Please try again.' }]);
                    setGenerating(false);
                }
            }, 2000);

        } catch (err) {
            toast.error(errorMessages['AI_GENERATION_ERROR']?.title || 'Generation failed. Please try again.');
            setMessages(prev => [...prev, { role: 'assistant', content: '⚠ Generation failed. Please try again.' }]);
            setGenerating(false);
        }
    };

    // ─── Asset picker render ─────────────────────────────────────────────────

    const renderAssetPicker = () => {
        const { formsSettings = [], businessContext, allTextContent = {} } = storeSettings;
        const imageAssets = assets.filter(a => a.type === 'Image');
        const videoAssets = assets.filter(a => a.type === 'Video' || a.type === 'Video Link');
        const pdfAssets = assets.filter(a => a.type === 'PDF');

        const isSelected = (id) => selectedAssets.some(a => {
            if (id === '__business_context__') return a._type === 'business_context';
            if (id === '__text_content__') return a._type === 'text_content';
            if (id.startsWith('form__')) return a._type === 'form' && `form__${a.form_id}` === id;
            if (id.startsWith('sec__')) return a._type === 'section' && `sec__${a.section_id}` === id;
            return a._id === id;
        });

        const showSpecial  = assetTypeFilter !== 'Sections';
        const showImages   = assetTypeFilter === 'All' || assetTypeFilter === 'Image';
        const showVideos   = assetTypeFilter === 'All' || assetTypeFilter === 'Video';
        const showPdfs     = assetTypeFilter === 'All' || assetTypeFilter === 'PDF';
        const showForms    = assetTypeFilter === 'All' || assetTypeFilter === 'Forms';
        const showSections = assetTypeFilter === 'All' || assetTypeFilter === 'Sections';

        const hasContent = (
            (showSpecial && (businessContext || (selectedPage && allTextContent[selectedPage]))) ||
            (showImages && imageAssets.length > 0) ||
            (showVideos && videoAssets.length > 0) ||
            (showPdfs && pdfAssets.length > 0) ||
            (showForms && formsSettings.length > 0) ||
            (showSections && importedSections.length > 0)
        );

        if (!hasContent) {
            return <div style={{ textAlign: 'center', padding: '20px 0', color: '#9aa2ad', fontSize: 13 }}>No assets available for this filter.</div>;
        }

        return (
            <>
                {showSpecial && (businessContext || (selectedPage && allTextContent[selectedPage])) && (
                    <AssetPickerGroup disabled={isConfigLocked}>
                        {businessContext && (
                            <AssetPickerCard id="__business_context__" name="Business Context" previewType="special"
                                assetObj={{ icon: '🏢' }} selected={isSelected('__business_context__')} disabled={isConfigLocked} onToggle={handleAssetToggle} />
                        )}
                        {selectedPage && allTextContent[selectedPage] && (
                            <AssetPickerCard id="__text_content__" name="Text Content" previewType="special"
                                assetObj={{ icon: '📄' }} selected={isSelected('__text_content__')} disabled={isConfigLocked} onToggle={handleAssetToggle} />
                        )}
                    </AssetPickerGroup>
                )}
                {showImages && imageAssets.length > 0 && (
                    <AssetPickerGroup label="Images" onSelectAll={imageAssets.length > 1 ? () => handleAssetToggle('choose_all_images') : null} disabled={isConfigLocked}>
                        {imageAssets.map(a => (
                            <AssetPickerCard key={a._id} id={a._id} name={a.title} previewType="image"
                                previewUrl={a.url || (a.fileName ? getFilePreviewUrl(a) : null)}
                                assetObj={a} selected={isSelected(a._id)} disabled={isConfigLocked} onToggle={handleAssetToggle} />
                        ))}
                    </AssetPickerGroup>
                )}
                {showVideos && videoAssets.length > 0 && (
                    <AssetPickerGroup label="Videos" onSelectAll={videoAssets.length > 1 ? () => handleAssetToggle('choose_all_videos') : null} disabled={isConfigLocked}>
                        {videoAssets.map(a => (
                            <AssetPickerCard key={a._id} id={a._id} name={a.title} previewType="video"
                                previewUrl={a.url} assetObj={a} selected={isSelected(a._id)} disabled={isConfigLocked} onToggle={handleAssetToggle} />
                        ))}
                    </AssetPickerGroup>
                )}
                {showPdfs && pdfAssets.length > 0 && (
                    <AssetPickerGroup label="PDFs" onSelectAll={pdfAssets.length > 1 ? () => handleAssetToggle('choose_all_pdfs') : null} disabled={isConfigLocked}>
                        {pdfAssets.map(a => (
                            <AssetPickerCard key={a._id} id={a._id} name={a.title} previewType="pdf"
                                assetObj={a} selected={isSelected(a._id)} disabled={isConfigLocked} onToggle={handleAssetToggle} />
                        ))}
                    </AssetPickerGroup>
                )}
                {showForms && formsSettings.length > 0 && (
                    <AssetPickerGroup label="Forms" disabled={isConfigLocked}>
                        {formsSettings.map(f => (
                            <AssetPickerCard key={f.form_id} id={`form__${f.form_id}`} name={f.label} previewType="form"
                                assetObj={{ ...f, _type: 'form' }} subLabel={f.fields?.length ? `${f.fields.length} fields` : undefined}
                                selected={isSelected(`form__${f.form_id}`)} disabled={isConfigLocked} onToggle={handleAssetToggle} />
                        ))}
                    </AssetPickerGroup>
                )}
                {showSections && importedSections.length > 0 && (
                    <AssetPickerGroup
                        label="Sections"
                        disabled={isConfigLocked}
                        containerStyle={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                            gap: 12,
                        }}
                    >
                        {importedSections.map(s => (
                            <AssetPickerCard key={s.section_id} id={`sec__${s.section_id}`} name={s.section_name || s.section_id}
                                previewType="section" previewUrl={s.url || null} assetObj={{ ...s, _type: 'section' }}
                                selected={isSelected(`sec__${s.section_id}`)} disabled={isConfigLocked} onToggle={handleAssetToggle} large />
                        ))}
                    </AssetPickerGroup>
                )}
            </>
        );
    };

    // ─── Render ──────────────────────────────────────────────────────────────

    const providerLabel = LLM_PROVIDERS[llmProvider]?.label || llmProvider;
    const modelLabel = LLM_PROVIDERS[llmProvider]?.models.find(m => m.value === llmModel)?.label || llmModel;
    const pageShort = selectedPage ? selectedPage.replace(/^https?:\/\//, '').slice(0, 35) : '—';

    return (
        <div className="main-container" style={{ position: 'relative' }}>
            {pageLoading ? (
                <div className="main_conatiner_spinner"><Spinner animation="border" /></div>
            ) : (
                <div className="ai-workspace">

                    {/* ── View toggle header ───────────────────────────────── */}
                    <div className="ai-workspace-header">
                        <div className="ai-view-toggle">
                            <button
                                className={`ai-view-tab${activeView === 'chat' ? ' active' : ''}`}
                                onClick={() => setActiveView('chat')}
                            >
                                🤖 Chat & Config
                            </button>
                            <button
                                className={`ai-view-tab${activeView === 'sections' ? ' active' : ''}`}
                                onClick={() => setActiveView('sections')}
                            >
                                Sections{generatedSections.length > 0 ? ` (${generatedSections.length})` : ''}
                                {generating && <Spinner animation="border" size="sm" className="ms-2" />}
                            </button>
                        </div>
                    </div>

                    {/* ── Chat & Config view ───────────────────────────────── */}
                    {activeView === 'chat' && (
                        <div className="ai-chat-panel">
                            {isConfigOpen ? (
                                /* Config form */
                                <div className="ai-config-fullscreen">
                                    <Form>
                                        {isConfigLocked && (
                                            <div className="alert alert-info py-2 px-3 mb-3" style={{ fontSize: 13 }}>
                                                Config is locked. Reset to make changes.
                                            </div>
                                        )}

                                        <Form.Group className="mb-3">
                                            <Form.Label className="mb-1 charts_filter_label">Select Page</Form.Label>
                                            <Form.Select value={selectedPage} onChange={e => setSelectedPage(e.target.value)} disabled={isConfigLocked}>
                                                <option value="">Select a landing page</option>
                                                {storeSettings.brandingPages.map(p => <option key={p} value={p}>{p}</option>)}
                                            </Form.Select>
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label className="mb-1 charts_filter_label">
                                                Assets
                                                {(outputType === 'forms' || mode === 'improve') && (
                                                    <span className="text-muted ms-2" style={{ fontSize: 11, fontWeight: 400 }}>
                                                        {outputType === 'forms' ? '(1 form required)' : '(1 section required)'}
                                                    </span>
                                                )}
                                            </Form.Label>
                                            <button
                                                type="button"
                                                onClick={() => !isConfigLocked && setIsPickerOpen(true)}
                                                disabled={isConfigLocked}
                                                style={{
                                                    display: 'flex', alignItems: 'center', gap: 8,
                                                    width: '100%', padding: '8px 12px',
                                                    border: '1px solid #dee2e6', borderRadius: 6,
                                                    background: isConfigLocked ? '#f8f9fa' : '#fff',
                                                    cursor: isConfigLocked ? 'default' : 'pointer',
                                                    fontSize: 13, textAlign: 'left',
                                                    color: selectedAssets.length > 0 ? '#212529' : '#6c757d',
                                                }}
                                            >
                                                <span>🗂️</span>
                                                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {selectedAssets.length > 0
                                                        ? selectedAssets.map(a => a.title || a.label || a.section_name || a._type).slice(0, 3).join(', ') + (selectedAssets.length > 3 ? ` +${selectedAssets.length - 3} more` : '')
                                                        : 'Click to select assets…'
                                                    }
                                                </span>
                                                {selectedAssets.length > 0 && <Badge bg="primary">{selectedAssets.length}</Badge>}
                                            </button>
                                        </Form.Group>

                                        {/* ── Asset Picker Modal ────────────── */}
                                        <Modal show={isPickerOpen} onHide={() => setIsPickerOpen(false)} size="lg" scrollable>
                                            <Modal.Header closeButton style={{ padding: '12px 16px' }}>
                                                <Modal.Title style={{ fontSize: 15, fontWeight: 600 }}>
                                                    Select Assets
                                                    {selectedAssets.length > 0 && (
                                                        <Badge bg="primary" className="ms-2">{selectedAssets.length} selected</Badge>
                                                    )}
                                                </Modal.Title>
                                            </Modal.Header>
                                            <Modal.Body style={{ padding: '12px 16px' }}>
                                                <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
                                                    {ASSET_TYPE_OPTIONS.map(o => (
                                                        <button
                                                            key={o.value}
                                                            type="button"
                                                            onClick={() => setAssetTypeFilter(o.value)}
                                                            style={{
                                                                padding: '4px 12px', borderRadius: 20,
                                                                border: assetTypeFilter === o.value ? '2px solid #0d6efd' : '1px solid #dee2e6',
                                                                background: assetTypeFilter === o.value ? '#e8f0fe' : '#fff',
                                                                color: assetTypeFilter === o.value ? '#0d6efd' : '#495057',
                                                                fontSize: 12, cursor: 'pointer',
                                                                fontWeight: assetTypeFilter === o.value ? 600 : 400,
                                                            }}
                                                        >
                                                            {o.label}
                                                        </button>
                                                    ))}
                                                </div>
                                                {renderAssetPicker()}
                                            </Modal.Body>
                                            <Modal.Footer style={{ justifyContent: 'space-between', padding: '10px 16px' }}>
                                                <span style={{ fontSize: 13, color: '#6c757d' }}>
                                                    {selectedAssets.length > 0
                                                        ? `${selectedAssets.length} asset${selectedAssets.length > 1 ? 's' : ''} selected`
                                                        : 'No assets selected'}
                                                </span>
                                                <Button className="sxi_primary_blue_btn" onClick={() => setIsPickerOpen(false)}>Done</Button>
                                            </Modal.Footer>
                                        </Modal>

                                        <Row className="mb-3">
                                            <Col md={6}>
                                                <Form.Label className="mb-1 charts_filter_label">Output Type</Form.Label>
                                                <Form.Select value={outputType} onChange={e => setOutputType(e.target.value)} disabled={isConfigLocked}>
                                                    {OUTPUT_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                                </Form.Select>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Label className="mb-1 charts_filter_label">Mode</Form.Label>
                                                <div className="d-flex gap-3 mt-2">
                                                    {[{ v: 'create', l: 'Create New' }, { v: 'improve', l: 'Improve Existing' }].map(opt => (
                                                        <Form.Check
                                                            key={opt.v}
                                                            type="radio"
                                                            id={`mode_${opt.v}`}
                                                            label={opt.l}
                                                            checked={mode === opt.v}
                                                            onChange={() => setMode(opt.v)}
                                                            disabled={isConfigLocked}
                                                        />
                                                    ))}
                                                </div>
                                            </Col>
                                        </Row>

                                        <Row className="mb-3">
                                            <Col md={6}>
                                                <Form.Label className="mb-1 charts_filter_label">AI Provider</Form.Label>
                                                <Form.Select value={llmProvider} onChange={handleProviderChange} disabled={isConfigLocked}>
                                                    {Object.entries(LLM_PROVIDERS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                                                </Form.Select>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Label className="mb-1 charts_filter_label">Model</Form.Label>
                                                <Form.Select value={llmModel} onChange={e => setLlmModel(e.target.value)} disabled={isConfigLocked}>
                                                    {LLM_PROVIDERS[llmProvider].models.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                                                </Form.Select>
                                            </Col>
                                        </Row>

                                        <div className="d-flex justify-content-end gap-2 mt-3 pt-2" style={{ borderTop: '1px solid #e9ecef' }}>
                                            {isConfigLocked
                                                ? <Button variant="outline-secondary" onClick={() => setIsConfigOpen(false)}>Close</Button>
                                                : <>
                                                    <Button variant="outline-danger" onClick={handleResetConfig}>Reset</Button>
                                                    <Button className="sxi_primary_blue_btn" onClick={handleSaveConfig}>Save Config & Chat →</Button>
                                                  </>
                                            }
                                            {isConfigLocked && (
                                                <Button variant="outline-danger" onClick={handleResetConfig}>Reset & Edit</Button>
                                            )}
                                        </div>
                                    </Form>
                                </div>
                            ) : (
                                /* Chat screen */
                                <>
                                    {isConfigLocked && (
                                        <div className="ai-config-chips">
                                            <span className="ai-chip" title={selectedPage}>{pageShort}</span>
                                            <span className="ai-chip">{outputType}</span>
                                            <span className="ai-chip">{mode}</span>
                                            {selectedAssets.length > 0 && <span className="ai-chip">{selectedAssets.length} asset{selectedAssets.length > 1 ? 's' : ''}</span>}
                                            <span className="ai-chip ai-chip-provider">{providerLabel} · {modelLabel}</span>
                                        </div>
                                    )}

                                    <div ref={messagesEndRef} className="ai-messages-area">
                                        {messages.map((msg, idx) =>
                                            msg.role === 'user'
                                                ? <div key={idx} className="ai-msg-user">{msg.content}</div>
                                                : (
                                                    <div key={idx} className="ai-msg-assistant-wrap">
                                                        <div className="ai-msg-avatar">🤖</div>
                                                        <div className="ai-msg-assistant"><pre>{msg.content}</pre></div>
                                                    </div>
                                                )
                                        )}
                                    </div>

                                    <form className="ai-input-row" onSubmit={handleGenerate}>
                                        <button
                                            type="button"
                                            className={`ai-config-icon-btn${isConfigOpen ? ' active' : ''}`}
                                            onClick={() => setIsConfigOpen(p => !p)}
                                            title={isConfigOpen ? 'Hide config' : 'Configure'}
                                        >
                                            ⚙
                                            {isConfigLocked && !isConfigOpen && (
                                                <span className="ai-config-icon-dot" />
                                            )}
                                        </button>
                                        <textarea
                                            ref={inputRef}
                                            className="ai-input-field"
                                            rows={1}
                                            placeholder={isConfigLocked ? 'Describe the content you want to generate...' : 'Configure your generator first...'}
                                            value={input}
                                            onChange={e => setInput(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleGenerate(e); } }}
                                            disabled={!isConfigLocked || generating}
                                        />
                                        <button
                                            className="ai-send-btn"
                                            type="submit"
                                            disabled={!isConfigLocked || generating || !input.trim()}
                                        >
                                            Generate →
                                        </button>
                                    </form>
                                </>
                            )}
                        </div>
                    )}

                    {/* ── Sections view ────────────────────────────────────── */}
                    {activeView === 'sections' && (
                        <div className="ai-sections-view">
                            {generating ? (
                                <div className="ai-sections-loading">
                                    <Spinner animation="border" style={{ width: 48, height: 48, color: 'var(--cux-heading, #132848)' }} />
                                    <p className="mt-3 mb-0" style={{ color: '#495057', fontWeight: 500 }}>Generating sections…</p>
                                    <small className="text-muted">This may take a moment</small>
                                </div>
                            ) : generatedSections.length === 0 ? (
                                <div className="ai-empty-state">
                                    <div style={{ fontSize: 48 }}>📄</div>
                                    <h5>No sections yet</h5>
                                    <p>Go to Chat & Config, write a prompt, and click Generate.</p>
                                    <Button className="sxi_primary_blue_btn" onClick={() => setActiveView('chat')}>
                                        Go to Chat →
                                    </Button>
                                </div>
                            ) : (
                                <div id="ai-generated-sections">
                                    {generatedSections.map((section, idx) => (
                                        <div
                                            key={section.section_id || idx}
                                            style={{
                                                position: 'relative',
                                                marginBottom: '1rem',
                                                border: '1px solid #e9ecef',
                                                borderRadius: '8px',
                                                overflow: 'hidden',
                                                backgroundColor: '#ffffff',
                                            }}
                                            className="sxi-existing-div-wrapper"
                                        >
                                            <div style={{
                                                padding: '8px 14px',
                                                background: '#f8f9fa',
                                                borderBottom: '1px solid #e9ecef',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 8,
                                                fontSize: 13,
                                            }}>
                                                <strong>{section.section_name}</strong>
                                                <Badge bg="primary" className="ms-1">{section.section_type}</Badge>
                                                {section.firebaseUrl && (
                                                    <a
                                                        href={section.firebaseUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="btn btn-sm btn-outline-secondary ms-auto"
                                                        style={{ fontSize: 12 }}
                                                    >
                                                        Open URL
                                                    </a>
                                                )}
                                            </div>
                                            <iframe
                                                srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>${section.css || ''}</style></head><body style="margin:0;padding:0">${section.html || ''}${section.js ? '<script>' + section.js + '<\/script>' : ''}</body></html>`}
                                                style={{ width: '100%', height: '480px', border: 'none', display: 'block' }}
                                                title={section.section_name || `section-${idx}`}
                                                sandbox="allow-scripts allow-same-origin"
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                </div>
            )}
        </div>
    );
}

export default AIAssetContentGenerator;
